import { useState } from 'react';
import {
  EngageLibraryRowModel,
  EngageLibrarySyncActionEnum,
  PendingMembershipChange,
} from './engage-library-types';
import {
  engageLibraryRowKey,
  membershipLockReason,
  pruneSettledMembershipChanges,
  tenantHasItem,
  toPendingMembershipChanges,
} from './engage-library-utils';

function setDesiredMembership(
  desiredMembershipByKey: Map<string, boolean>,
  row: EngageLibraryRowModel,
  desiredMembership: boolean,
) {
  const key = engageLibraryRowKey(row);
  const matchesServer = desiredMembership === tenantHasItem(row);
  if (matchesServer) desiredMembershipByKey.delete(key);
  else desiredMembershipByKey.set(key, desiredMembership);
}

export interface PendingMembershipChanges {
  changes: PendingMembershipChange[];
  pendingActionByKey: Map<string, EngageLibrarySyncActionEnum>;
  toggle: (row: EngageLibraryRowModel) => void;
  /** Checks or unchecks every given row, skipping locked ones. */
  setMembership: (rows: EngageLibraryRowModel[], desiredMembership: boolean) => void;
  discard: () => void;
  retain: (changesToKeep: PendingMembershipChange[]) => void;
}

/**
 * Tracks the tenant memberships the partner has checked or unchecked but not saved, layered over
 * the library's server state and keyed across both type tabs so filters never drop a change.
 */
export function usePendingMembershipChanges(
  libraryRows: EngageLibraryRowModel[],
): PendingMembershipChanges {
  const [desiredMembershipByKey, setDesiredMembershipByKey] = useState(() => new Map<string, boolean>());

  // Adjusting state during render: a library refetch that already matches a change settles it
  // here, so a later server flip back cannot resurrect the stale change.
  const openMembershipByKey = pruneSettledMembershipChanges(desiredMembershipByKey, libraryRows);
  if (openMembershipByKey !== desiredMembershipByKey) {
    setDesiredMembershipByKey(openMembershipByKey);
  }

  const changes = toPendingMembershipChanges(openMembershipByKey, libraryRows);
  const pendingActionByKey = new Map(
    changes.map(change => [engageLibraryRowKey(change.row), change.action]),
  );

  const toggle = (row: EngageLibraryRowModel) => {
    if (membershipLockReason(row)) return;
    const key = engageLibraryRowKey(row);
    setDesiredMembershipByKey(previous => {
      const next = new Map(previous);
      setDesiredMembership(next, row, !(previous.get(key) ?? tenantHasItem(row)));
      return next;
    });
  };

  const setMembership = (rows: EngageLibraryRowModel[], desiredMembership: boolean) => {
    const changeableRows = rows.filter(row => !membershipLockReason(row));
    setDesiredMembershipByKey(previous => {
      const next = new Map(previous);
      for (const row of changeableRows) setDesiredMembership(next, row, desiredMembership);
      return next;
    });
  };

  const discard = () => setDesiredMembershipByKey(new Map());

  const retain = (changesToKeep: PendingMembershipChange[]) =>
    setDesiredMembershipByKey(
      new Map(
        changesToKeep.map(change => [
          engageLibraryRowKey(change.row),
          change.action === EngageLibrarySyncActionEnum.SYNC,
        ]),
      ),
    );

  return { changes, pendingActionByKey, toggle, setMembership, discard, retain };
}
